import React from 'react';
import { cn } from './cn';

/**
 * The five platform marks, in one place.
 *
 * These lived inline in ConnectAccounts and nowhere else, so every other
 * screen identified a platform with a coloured dot plus its name. A dot is a
 * legend you have to learn; the mark is the thing people already recognise
 * from their phone's home screen, and it reads at 14px where a word does not.
 *
 * **Monochrome by default, brand colour only on request.** The design system
 * is explicit that chrome stays monochrome so thumbnails and charts carry the
 * colour, and that brand colour belongs on /connect where picking a platform
 * IS the task. A logo does not need its brand colour to be recognised — the
 * shape is the identity — so the default inherits `currentColor` and fits
 * anywhere. `tone="brand"` is for /connect and the connect chooser.
 *
 * Every mark is drawn in a 24×24 box (Threads is natively 192, scaled by its
 * own viewBox) so they optically match at one size without per-platform
 * nudging.
 */

const MARKS = {
  tiktok: {
    label: 'TikTok',
    viewBox: '0 0 24 24',
    path: 'M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z',
  },
  instagram: {
    label: 'Instagram',
    viewBox: '0 0 24 24',
    path: 'M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z',
  },
  facebook: {
    label: 'Facebook',
    viewBox: '0 0 24 24',
    path: 'M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z',
  },
  threads: {
    label: 'Threads',
    viewBox: '0 0 192 192',
    path: 'M141.537 88.9883C140.71 88.5919 139.87 88.2104 139.019 87.8451C137.537 60.5382 122.616 44.905 97.5619 44.745C97.4484 44.7443 97.3355 44.7443 97.222 44.745C81.8483 44.745 69.2318 51.1399 61.2605 62.0849L75.4691 71.7006C81.3369 63.3395 90.2658 62.3498 97.2804 62.3498C97.3507 62.3498 97.4217 62.3498 97.492 62.3504C105.281 62.4 111.348 64.7217 115.574 69.2693C118.603 72.5377 120.696 76.975 121.834 82.523C115.097 81.3173 107.753 80.8364 99.874 81.0934C78.1099 81.7658 64.1584 93.9698 65.0416 110.994C65.4863 119.6 69.7422 127.063 77.0026 132.07C83.1633 136.326 91.0945 138.443 99.3873 138.024C110.178 137.474 118.714 133.262 124.721 125.486C129.289 119.586 132.262 112.045 133.764 102.577C138.849 105.555 142.672 109.553 144.877 114.498C148.555 122.591 148.754 135.49 138.666 145.544C129.768 154.41 119.077 158.274 102.685 158.398C84.4103 158.261 70.5765 152.61 61.6524 141.413C53.3159 130.959 48.9655 115.88 48.7996 96.0224C48.9655 76.1648 53.3159 61.0854 61.6524 50.6322C70.5765 39.4352 84.4103 33.7837 102.685 33.647C121.086 33.7854 135.125 39.4969 144.287 50.7796C148.756 56.2823 152.15 63.0582 154.427 70.9572L170.867 66.7659C168.098 56.8539 163.832 48.37 158.08 41.4469C146.318 27.1892 129.488 19.8979 107.529 19.6597L102.627 19.6472L97.7254 19.6597C75.766 19.8979 58.9361 27.1892 47.1744 41.4469C36.3961 54.6055 30.8036 72.6835 30.5947 96.0022L30.5938 96.335L30.5947 96.6679C30.8036 119.987 36.3961 138.065 47.1744 151.223C58.9361 165.481 75.766 172.772 97.7254 173.01L102.627 173.023L107.529 173.01C127.971 172.856 142.07 167.383 153.518 155.972C168.702 140.822 168.281 121.927 163.216 110.489C159.559 102.242 152.329 95.5583 141.537 88.9883ZM98.4405 120.473C88.8697 120.948 82.6822 115.434 82.3556 109.406C82.1017 104.606 85.4236 98.4397 100.347 97.9019C102.243 97.8365 104.104 97.8057 105.932 97.8057C111.587 97.8057 116.899 98.3107 121.807 99.2841C120.076 117.599 109.957 119.9 98.4405 120.473Z',
  },
  twitter: {
    label: 'X',
    viewBox: '0 0 24 24',
    path: 'M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z',
  },
};

// Brand colour per platform, as a token — never a literal. TikTok, Threads
// and X are black or white in their own guidelines; on this canvas black
// disappears, which is why those tokens resolve to something visible instead.
const BRAND_COLOR = {
  tiktok: 'var(--color-brand-tiktok)',
  instagram: 'var(--color-brand-instagram)',
  facebook: 'var(--color-brand-facebook)',
  threads: 'var(--color-brand-threads)',
  twitter: 'var(--color-brand-twitter)',
};

export const hasPlatformLogo = (platform) => !!MARKS[platform];

/**
 * @param platform  tiktok | instagram | facebook | threads | twitter
 * @param tone      'inherit' (default, takes currentColor) or 'brand'
 * @param label     override the accessible name; pass null for decorative,
 *                  which is right when the platform name is already beside it
 * @param className sizing and layout — `size-4` is the usual inline size
 */
export const PlatformLogo = ({
  platform, tone = 'inherit', label, className, ...props
}) => {
  const mark = MARKS[platform];
  // An unknown platform renders NOTHING rather than a placeholder glyph. A
  // "?" tile beside a real account reads as an error with the account.
  if (!mark) return null;

  const name = label === null ? undefined : (label || mark.label);
  return (
    <svg
      viewBox={mark.viewBox}
      fill="currentColor"
      role={name ? 'img' : undefined}
      aria-label={name}
      aria-hidden={name ? undefined : 'true'}
      className={cn('size-4 shrink-0', className)}
      style={tone === 'brand' ? { color: BRAND_COLOR[platform] } : undefined}
      {...props}
    >
      {name && <title>{name}</title>}
      <path d={mark.path} />
    </svg>
  );
};

export default PlatformLogo;
